import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

/* ─── SEO content ─────────────────────────────────────────────────
 * Update SITE_URL once you have your real domain so canonical and
 * social-share URLs are correct. */
const SITE_URL = "https://canadianretireplan.ca";
const PAGE_PATH = "/";
const PAGE_TITLE =
  "CanadianRetirePlan — Free Retirement Planning Tools for Canadians";
const PAGE_DESC =
  "Free Canadian retirement planning tools. Estimate spending, test readiness, and build a full income-projection plan covering CPP, OAS, RRSP, TFSA, and 2026 tax brackets. No sign-up required.";
const OG_IMAGE = `${SITE_URL}/og-image.png`; // 1200×630 recommended

const HERO_VIDEO_ID = "wmC2tVI-q-A";
const HERO_VIDEO_TITLE =
  "5 Retirement Questions Every Canadian Should Answer Before Retiring";
const HERO_VIDEO_DESC =
  "The essential questions to work through before you hand in your notice — income, lifestyle, healthcare, and legacy.";

/* The three-step user journey — each card links to its tool */
const STEPS = [
  {
    n: "01",
    to: "/estimator",
    title: "Understand Your Goals",
    blurb:
      "Pick a retirement lifestyle and see what each phase will actually cost. Go-Go, Slow-Go, and No-Go spending by category, built on StatsCan 2023 data.",
    tool: "Estimator",
    eta: "60 seconds",
  },
  {
    n: "02",
    to: "/guided-planner",
    title: "Test Your Readiness",
    blurb:
      "Answer plain-English questions about your savings, income, and timeline. Get a clear yes/no verdict and a prioritized list of next steps.",
    tool: "Guided Planner",
    eta: "5 minutes",
  },
  {
    n: "03",
    to: "/detailed-planner",
    title: "Build Your Full Plan",
    blurb:
      "Optimize CPP and OAS timing, model RRSP/TFSA drawdown strategies, stress-test inflation, and print a complete advisor-grade report.",
    tool: "Detailed Planner",
    eta: "20–30 minutes",
  },
];

/* JSON-LD structured data — Google's preferred schema format.
 * Bundled as a single @graph so multiple schema types stay related. */
const STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: "CanadianRetirePlan",
      description: PAGE_DESC,
      inLanguage: "en-CA",
    },
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "CanadianRetirePlan",
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
    },
    {
      "@type": "WebApplication",
      "@id": `${SITE_URL}/#app`,
      name: "CanadianRetirePlan Suite",
      url: SITE_URL,
      description: PAGE_DESC,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Any (Web)",
      browserRequirements: "Requires JavaScript. Modern browser.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "CAD",
        availability: "https://schema.org/InStock",
      },
      featureList: [
        "Retirement Spending Estimator",
        "Guided Retirement Readiness Interview",
        "12-section Detailed Retirement Planner",
        "CPP and OAS Timing Optimizer",
        "RRSP and TFSA Drawdown Strategy Builder",
        "2026 CRA Tax Brackets",
        "Curated Video Library",
      ],
      inLanguage: "en-CA",
      isAccessibleForFree: true,
    },
    {
      "@type": "VideoObject",
      name: HERO_VIDEO_TITLE,
      description: HERO_VIDEO_DESC,
      thumbnailUrl: `https://i.ytimg.com/vi/${HERO_VIDEO_ID}/maxresdefault.jpg`,
      embedUrl: `https://www.youtube.com/embed/${HERO_VIDEO_ID}`,
      uploadDate: "2024-01-01",
    },
  ],
};

/* Helper: idempotently set a <meta> tag in <head> */
function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return el;
}
/* Helper: idempotently set <link rel="..."> */
function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export default function Home() {
  /* Set page-specific meta tags on mount.
   * (For social-share crawlers that don't run JS — Facebook, Twitter,
   * LinkedIn — you also need these in public/index.html. See README.) */
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    setMeta("name", "description", PAGE_DESC);
    setMeta("property", "og:title", PAGE_TITLE);
    setMeta("property", "og:description", PAGE_DESC);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", SITE_URL + PAGE_PATH);
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("property", "og:locale", "en_CA");
    setMeta("property", "og:site_name", "CanadianRetirePlan");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", PAGE_TITLE);
    setMeta("name", "twitter:description", PAGE_DESC);
    setMeta("name", "twitter:image", OG_IMAGE);
    setLink("canonical", SITE_URL + PAGE_PATH);

    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <>
      <main className="home" role="main">
        {/* ────── HERO ────── */}
        <header className="home-hero">
          <p className="eyebrow">For Canadians · No sign-up</p>
          <h1 className="hero-title">
            Get clarity on your <br />
            <em>Retirement Future</em>
          </h1>
          <p className="hero-lede">
            Three free tools built for Canadians: a 60-second spending estimate,
            a guided readiness interview, and a full income-projection suite
            covering CPP, OAS, RRSP, TFSA, and 2026 tax brackets.
          </p>

          <figure className="hero-video">
            <div className="hero-video-frame">
              <iframe
                src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}`}
                title={HERO_VIDEO_TITLE}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </figure>
        </header>

        {/* ────── JOURNEY ────── */}
        <section className="home-journey" aria-labelledby="journey-heading">
          <div className="section-head">
            <p className="eyebrow">The Three-Step Path</p>
            <h2 id="journey-heading">
              From <em>"I don't know"</em> to <em>"I'm ready"</em>
            </h2>
            <p className="section-sub">
              Each step builds on the last. You can start anywhere, but most
              people get the most benefit by following them in order.
            </p>
          </div>

          <div
            className="journey-flow"
            role="list"
            aria-label="Retirement planning steps"
          >
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                {i > 0 && (
                  <div className="journey-arrow" aria-hidden="true">
                    <svg viewBox="0 0 56 16" width="56" height="16" fill="none">
                      <line
                        x1="2"
                        y1="8"
                        x2="44"
                        y2="8"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeDasharray="3 4"
                        strokeLinecap="round"
                      />
                      <polyline
                        points="40,2 50,8 40,14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </div>
                )}
                <Link to={s.to} className="step-card" role="listitem">
                  <article>
                    <div className="step-card-head">
                      <span className="step-num">{s.n}</span>
                      <span
                        className="step-eta"
                        title="Approximate time to complete"
                      >
                        ≈ {s.eta}
                      </span>
                    </div>
                    <h3 className="step-title">{s.title}</h3>
                    <p className="step-blurb">{s.blurb}</p>
                    <span className="step-tool">
                      Open <strong>{s.tool}</strong>
                      <span aria-hidden="true"> →</span>
                    </span>
                  </article>
                </Link>
              </React.Fragment>
            ))}
          </div>

          <div className="journey-bottom">
            <Link to="/estimator" className="btn btn-large">
              <span className="btn-large-main">
                Let's start your first step
              </span>
              <span className="btn-large-sub">
                Takes about 60 seconds — no sign-up
              </span>
            </Link>
          </div>
        </section>

        {/* ────── VIDEO LIBRARY TEASER ────── */}
        <section className="home-video-teaser" aria-labelledby="videos-heading">
          <div className="video-teaser-inner">
            <p className="eyebrow">Prefer Watching to Reading?</p>
            <h2 id="videos-heading">A short, curated video library</h2>
            <p className="section-sub">
              Bite-size videos covering the retirement smile, lifestyle
              archetypes, and the essential questions every Canadian should
              answer before retiring.
            </p>
            <Link to="/videos" className="btn btn-secondary">
              Browse the Video Library
              <span aria-hidden="true"> →</span>
            </Link>
          </div>
        </section>
      </main>

      {/* JSON-LD structured data island — invisible to humans, gold to search engines.
          `dangerouslySetInnerHTML` is the recommended pattern (avoids React's quote
          escaping which breaks some schema parsers). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA) }}
      />
    </>
  );
}
